import { useEffect, useRef } from 'react';

// Components
import { Dropdown, SMPFlex, SMPText, useSizeContext, styled } from '../../../index';

// Utils
import { scrollToHorizontally } from '@sportal365/smp-widget-utils';
import { USER_INTERACTION } from '../../../../constants/entityTypes';
import { defaultHandleUserInteractionClick } from '@sportal365/smp-widget-utils/src/clickHandlers';
import { TAB_SELECTION_SUBSCRIBE_TYPE } from '../../../../constants/subscribeTypes';
import { ISubscriberInfo } from '../../../../types';

const PillButton = styled.withConfig({ componentId: 'PillTabs', displayName: 'PillButton' })('button', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 0,
  variants: {
    mode: {
      tabPill: {
        bc: '$pillBackgroundColor',
        border: '1px solid $pillBorderColor',
      },
      tabPillActive: {
        bc: '$pillActiveBackgroundColor',
      },
      tabPillDisabled: {
        bc: '$pillBackgroundColor',
        opacity: 0.5,
        pointerEvents: 'none',
        border: '1px solid $pillBorderColor',
      },
    },
  },
  borderRadius: '$5',
  fontFamily: '$sans',
  lineHeight: '20px',
  '-webkit-appearance': 'none',
  '-moz-appearance': 'none',
  ':-ms-expand': {
    display: 'none',
  },
  cursor: 'pointer',
  border: 'none',
  outline: 'none',
  '&:hover': {
    cursor: 'pointer',
  },
  '&:not(:last-child)': {
    mr: '$1',
  },
});

interface IPillTab {
  name: string;
  value: string;
  disabled?: boolean;
}

export interface IPillTabsProps {
  tabs: IPillTab[];
  handleSubscriptionClick: (info: ISubscriberInfo, clickType: string) => void;
  selectedTab?: string;
  handleTabClick?: (tab: string) => void;
  mobileDropdown?: boolean;
  parentRef?: React.RefObject<HTMLDivElement | null>;
}

export const PillTabs = ({
  tabs,
  selectedTab = '',
  handleTabClick,
  handleSubscriptionClick,
  mobileDropdown,
  parentRef,
}: IPillTabsProps): JSX.Element => {
  const activeTabRef = useRef<HTMLButtonElement>(null);
  const { isMobile } = useSizeContext();

  useEffect(() => {
    scrollToHorizontally(activeTabRef?.current);
  }, []);

  const handleTabClickAndSendInteractionEvent = (tab: string) => {
    handleTabClick && handleTabClick(tab);
    defaultHandleUserInteractionClick(handleSubscriptionClick, USER_INTERACTION, TAB_SELECTION_SUBSCRIBE_TYPE);
  };

  if (mobileDropdown) {
    const activeTab = tabs.find((tab) => tab.value === selectedTab.toString());
    return (
      <div style={{ width: '100%' }}>
        <Dropdown
          options={tabs.map((tab) => ({ label: tab.name, value: tab.value }))}
          value={activeTab ? activeTab.value : undefined}
          onChange={(value) => {
            if (value) {
              handleTabClickAndSendInteractionEvent(value);
            }
          }}
          handleSubscriptionClick={handleSubscriptionClick}
          parentRef={parentRef}
        />
      </div>
    );
  }

  return (
    <SMPFlex variant='tabsContainer'>
      <SMPFlex>
        {tabs.map((currentTab, index) => {
          const isActive = currentTab.value === selectedTab.toString();

          if (!currentTab?.name) {
            // do not render tabs without text
            return null;
          }

          return (
            <PillButton
              key={`filter-${index}`}
              ref={isActive ? activeTabRef : undefined}
              mode={isActive ? 'tabPillActive' : currentTab.disabled ? 'tabPillDisabled' : 'tabPill'}
              style={{ height: isMobile ? '32px' : '42px' }}
            >
              <SMPText
                variant={isActive ? 'activeTabLabel' : 'tabLabel'}
                onClick={() => handleTabClickAndSendInteractionEvent(currentTab.value)}
                style={{
                  lineHeight: '20px',
                  fontSize: isMobile ? '12px' : '14px',
                  paddingInline: isMobile ? '12px' : '24px',
                }}
              >
                {currentTab.name}
              </SMPText>
            </PillButton>
          );
        })}
      </SMPFlex>
    </SMPFlex>
  );
};
